// Client side of the Velocity Metrics targets (Tab, Oct 27 2026): weekly windows, the live actuals, and status scoring.
import { useEffect, useMemo, useState } from "react";
import { VELOCITY_INPUTS, type ActualKey, type Direction, type MetricKind, type VelocityFunnel, type VelocityPlan, type VelocityTargetRow } from "../config/velocity-targets";

export type VelocityActuals = {
  optins: number; apps: number; fullApps: number; partialApps: number; bookedIB: number; bookedOB: number;
  cancellations: number | null; eocs: number; shown: number; closed: number; cash: number; newCash: number; mrrCash: number; revenue: number;
  stlMedian: number | null; stlWithin1h: number | null;
};
export type VelocityWindow = { startDate: string; endDate: string; label: string; days: number };
export type VelocityColumn = VelocityWindow & Record<VelocityFunnel, VelocityActuals>;

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y!, (m! - 1), d!); };
const addDays = (d: Date, k: number) => { const x = new Date(d); x.setDate(x.getDate() + k); return x; };
const dayCount = (a: string, b: string) => Math.round((parse(b).getTime() - parse(a).getTime()) / 86400000) + 1;
const short = (s: string) => parse(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const MAX_WEEKS = 8;

/** Mon–Sun weeks inside the dashboard's date range. "All Time" shows the last 4 full weeks + this week. */
export function velocityWindows(startDate: string, endDate: string) {
  const today = iso(new Date());
  let start = startDate; let end = endDate > today ? today : endDate;
  let allTime = false;
  if (Number(startDate.slice(0, 4)) <= 2000) {
    allTime = true; const t = parse(today); const monday = addDays(t, -((t.getDay() + 6) % 7)); start = iso(addDays(monday, -28)); end = today;
  }
  if (start > end) start = end;
  const weeks: VelocityWindow[] = [];
  const s = parse(start); let mon = addDays(s, -((s.getDay() + 6) % 7));
  while (iso(mon) <= end) {
    const ws = iso(mon) < start ? start : iso(mon); const sun = iso(addDays(mon, 6)); const we = sun > end ? end : sun;
    weeks.push({ startDate: ws, endDate: we, label: `${short(ws)}–${short(we)}${we === today && sun !== today ? " (to date)" : ""}`, days: dayCount(ws, we) });
    mon = addDays(mon, 7);
  }
  const range: VelocityWindow = { startDate: start, endDate: end, label: "Range", days: dayCount(start, end) };
  const weekly = weeks.length <= MAX_WEEKS;
  const caption = `${allTime ? "Date range is All Time, so this shows the last 4 full weeks + this week: " : ""}${short(start)} – ${short(end)}, ${range.days} days${weekly ? "" : ` (${weeks.length} weeks: pick ${MAX_WEEKS} weeks or fewer to see weekly columns)`}`;
  return { weeks: weekly ? weeks : [], range, caption };
}

/** Loads a /api/velocity-metrics URL. The server answers saved numbers at once; when it is refreshing them in the
 *  background (`stale: true`), they are shown right away (onData(data, true)) and the same URL is asked again with
 *  fresh=1, which waits for that refresh, so the up-to-date numbers replace them (onData(data, false)). (Tab, Oct 29 2026,
 *  Joey: "Is there any way that you can have these velocity metric sheets load quicker?") */
export async function fetchVelocity<T>(url: string, signal: AbortSignal, onData: (data: T, updating: boolean) => void): Promise<void> {
  const load = async (target: string) => {
    const response = await fetch(target, { signal }); const body = await response.json();
    if (!response.ok || !body.success) throw new Error(body.error || "Unable to load velocity metrics");
    return body as { data: T; stale?: boolean };
  };
  const first = await load(url);
  if (!first.stale) { onData(first.data, false); return; }
  onData(first.data, true);
  try { onData((await load(`${url}${url.includes("?") ? "&" : "?"}fresh=1`)).data, false); }
  catch (err) { if ((err as Error)?.name === "AbortError") throw err; onData(first.data, false); }
}

/** Loads the actuals for every week plus the whole range (one request). */
/** traffic (Tab, Oct 29 2026): "all" = Blended (unchanged); "paid" / "organic" = the same windows on Meta-ads / other people only. */
export function useVelocityActuals(startDate: string, endDate: string, enabled = true, traffic: "all" | "paid" | "organic" = "all") {
  const { weeks, range, caption } = useMemo(() => velocityWindows(startDate, endDate), [startDate, endDate]);
  const [state, setState] = useState<{ columns: VelocityColumn[]; total: VelocityColumn | null; loading: boolean; error: string; updating?: boolean }>({ columns: [], total: null, loading: true, error: "" });
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const abort = new AbortController(); let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; abort.abort(); }, 120000);
    const sameAsRange = weeks.length === 1 && weeks[0]!.startDate === range.startDate && weeks[0]!.endDate === range.endDate;
    const list = sameAsRange ? [range] : [...weeks, range];
    setState((s) => ({ ...s, loading: true, error: "" }));
    fetchVelocity<Array<Record<VelocityFunnel, VelocityActuals>>>(`/api/velocity-metrics?windows=${encodeURIComponent(list.map((w) => `${w.startDate}_${w.endDate}`).join(","))}${traffic !== "all" ? `&traffic=${traffic}` : ""}`, abort.signal, (data, updating) => {
        const cols = list.map((w, i) => ({ ...w, ...data[i]! })) as VelocityColumn[];
        const total = cols[cols.length - 1]!;
        setState({ columns: sameAsRange ? [{ ...total, label: weeks[0]!.label }] : cols.slice(0, -1), total, loading: false, error: "", updating });
      })
      .catch((err) => { if (err.name !== "AbortError" || timedOut) setState({ columns: [], total: null, loading: false, error: timedOut ? "Velocity metrics took too long to load. Try a shorter date range." : err.message || "Unable to load velocity metrics", updating: false }); })
      .finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); abort.abort(); };
  }, [weeks, range, reload, enabled, traffic]);
  return { ...state, weeks, range, caption, reload: () => setReload((x) => x + 1) };
}

const div = (a: number, b: number) => (b > 0 ? a / b : null);
const pc = (a: number, b: number) => (b > 0 ? (a / b) * 100 : null);
/** Every actual a row can show, from one funnel's counts. Rates are recomputed from the sums, never averaged. */
export function deriveActuals(a: VelocityActuals | undefined): Partial<Record<ActualKey, number | null>> {
  if (!a) return {};
  const booked = a.bookedIB + a.bookedOB;
  return {
    optins: a.optins, apps: a.apps, fullApps: a.fullApps, partialApps: a.partialApps, bookedIB: a.bookedIB, bookedOB: a.bookedOB, totalBooked: booked,
    cancellations: a.cancellations, cancelRate: a.cancellations === null ? null : pc(a.cancellations, booked + a.cancellations),
    eocs: a.eocs, shown: a.shown, noShows: Math.max(0, a.eocs - a.shown), closed: a.closed, cash: a.cash, newCash: a.newCash, mrrCash: a.mrrCash, revenue: a.revenue,
    showRate: pc(a.shown, a.eocs), closeRate: pc(a.closed, a.shown), bookedToTaken: pc(a.shown, booked), bookingRate: pc(booked, a.apps), ibBookingRate: pc(a.bookedIB, a.apps),
    optinToApp: pc(a.apps, a.optins), optinToBooking: pc(booked, a.optins), optinToFullApp: pc(a.fullApps, a.optins), fullAppToBooking: pc(booked, a.fullApps),
    leadToClose: pc(a.closed, a.apps), bookedToClose: pc(a.closed, booked),
    // As in the UU velocity sheet Jeremy's 6-month actuals came from: ASP, cash/call and cash-vs-rev use NEW cash (closes only print new cash);
    // cash per application / booked call use all cash collected.
    asp: div(a.newCash, a.closed), cashVsRev: pc(a.newCash, a.revenue), cashPerCall: div(a.newCash, a.shown), cashPerApp: div(a.cash, a.apps), cashPerBooked: div(a.cash, booked),
    stlMedian: a.stlMedian, stlWithin1h: a.stlWithin1h, newCashShare: pc(a.newCash, a.cash),
  };
}

const int = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const one = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 0 });
export function formatValue(kind: MetricKind, v: number | null | undefined, target = false): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (kind === "money" || kind === "avg") return `$${int.format(Math.round(v))}`;
  if (kind === "rate") return `${one.format(v)}%`;
  if (kind === "minutes") return v >= 120 ? `${one.format(v / 60)} h` : `${int.format(Math.round(v))} min`;
  return target || !Number.isInteger(v) ? (Math.abs(v) < 100 ? one.format(v) : int.format(Math.round(v))) : int.format(v);
}

export type Tone = "good" | "warn" | "bad" | "none";
/** % of target and its tone. min: ≥100% green, ≥85% amber, else red. max: ≤100% green, ≤115% amber, else red. */
export function scoreTarget(actual: number | null | undefined, target: number | null | undefined, dir: Direction | undefined): { pct: number | null; tone: Tone } {
  if (actual === null || actual === undefined || target === null || target === undefined || !Number.isFinite(actual) || !(target > 0) || dir === "none") return { pct: actual != null && target ? (actual / target) * 100 : null, tone: "none" };
  const p = (actual / target) * 100;
  if (dir === "max") return { pct: p, tone: p <= 100 ? "good" : p <= 115 ? "warn" : "bad" };
  return { pct: p, tone: p >= 100 ? "good" : p >= 85 ? "warn" : "bad" };
}

/** A row's target for a period of `days` days: volume = weekly × days ÷ 7; levels as they are; text targets score on min/max. */
export function rowTarget(row: VelocityTargetRow, plan: VelocityPlan, days = 7): { weekly: number | null; period: number | null; monthly: number | null; text: string | null; monthlyText: string | null; dir: Direction | undefined; scoreOn: number | null } {
  const t = row.target;
  if (t.type === "volume") { const w = t.value(plan); return { weekly: w, period: (w * days) / 7, monthly: w * VELOCITY_INPUTS.weeksPerMonth, text: null, monthlyText: null, dir: t.dir, scoreOn: (w * days) / 7 }; }
  if (t.type === "level") { const v = t.value(plan); return { weekly: v, period: v, monthly: v, text: null, monthlyText: null, dir: t.dir, scoreOn: v }; }
  const text = typeof t.text === "function" ? t.text(plan) : t.text;
  const bound = t.min ?? t.max ?? null;
  return { weekly: null, period: null, monthly: null, text, monthlyText: t.monthly ?? text, dir: t.min != null ? "min" : t.max != null ? "max" : "none", scoreOn: bound };
}

export const TONE_STYLE: Record<Tone, { color: string; bg: string; label: string }> = {
  good: { color: "#16a34a", bg: "rgba(22,163,74,0.12)", label: "On track" },
  warn: { color: "#C99860", bg: "rgba(201,152,96,0.18)", label: "Close" },
  bad: { color: "#dc2626", bg: "rgba(220,38,38,0.12)", label: "Behind" },
  none: { color: "var(--muted-foreground)", bg: "transparent", label: "—" },
};
/** Urban Unity brand palette. */
export const UU = { gold: "#C99860", light: "#F2CC93", bronze: "#8C6236", brown: "#503215", black: "#0A0602" };
